import { useState, useEffect } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';

const BannerFormModal = ({ isOpen, onClose, banner, organizationId, onSuccess }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        link_url: '',
        media_type: 'image',
        display_duration: 5,
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        active_days: [],
        status: 'active'
    });
    const [media, setMedia] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const days = [
        { value: 'monday', label: 'Pazartesi' },
        { value: 'tuesday', label: 'Salı' },
        { value: 'wednesday', label: 'Çarşamba' },
        { value: 'thursday', label: 'Perşembe' },
        { value: 'friday', label: 'Cuma' },
        { value: 'saturday', label: 'Cumartesi' },
        { value: 'sunday', label: 'Pazar' }
    ];

    useEffect(() => {
        if (banner) {
            setFormData({
                title: banner.title || '',
                description: banner.description || '',
                link_url: banner.link_url || '',
                media_type: banner.media_type || 'image',
                display_duration: banner.display_duration || 5,
                start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
                end_date: banner.end_date ? banner.end_date.split('T')[0] : '',
                start_time: banner.start_time || '',
                end_time: banner.end_time || '',
                active_days: banner.active_days ? (Array.isArray(banner.active_days) ? banner.active_days : JSON.parse(banner.active_days)) : [],
                status: banner.status || 'active'
            });
            setMediaPreview(banner.media_url || null);
        } else {
            resetForm();
        }
    }, [banner, isOpen]);

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            link_url: '',
            media_type: 'image',
            display_duration: 5,
            start_date: '',
            end_date: '',
            start_time: '',
            end_time: '',
            active_days: [],
            status: 'active'
        });
        setMedia(null);
        setMediaPreview(null);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDayToggle = (day) => {
        setFormData(prev => ({
            ...prev,
            active_days: prev.active_days.includes(day)
                ? prev.active_days.filter(d => d !== day)
                : [...prev.active_days, day]
        }));
    };

    const handleMediaChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
        if (!validTypes.includes(file.type)) {
            setError('Geçersiz dosya formatı. Sadece JPG, PNG, GIF, MP4 veya WebM formatları desteklenir.');
            e.target.value = ''; // Reset input
            return;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setError('Dosya boyutu çok büyük. Maksimum 10MB olmalıdır.');
            e.target.value = ''; // Reset input
            return;
        }

        setError(null);
        setMedia(file);
        const reader = new FileReader();
        reader.onerror = () => {
            setError('Dosya okunurken bir hata oluştu.');
        };
        reader.onloadend = () => {
            setMediaPreview(reader.result);
        };
        reader.readAsDataURL(file);
        
        // Auto-detect media type
        if (file.type.startsWith('video/')) {
            setFormData(prev => ({ ...prev, media_type: 'video' }));
        } else if (file.type === 'image/gif') {
            setFormData(prev => ({ ...prev, media_type: 'gif' }));
        } else {
            setFormData(prev => ({ ...prev, media_type: 'image' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate required fields
            if (!media && !banner) {
                setError('Lütfen bir görsel veya video yükleyin');
                setLoading(false);
                return;
            }

            const formDataToSend = new FormData();
            
            // Always include media_type
            if (formData.media_type) {
                formDataToSend.append('media_type', formData.media_type);
            }
            
            Object.keys(formData).forEach(key => {
                if (key === 'active_days') {
                    if (formData[key] && formData[key].length > 0) {
                        formDataToSend.append(key, JSON.stringify(formData[key]));
                    }
                } else if (key !== 'media_type' && formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            });
            if (media) {
                formDataToSend.append('media', media);
            }

            const url = banner
                ? `/organizations/${organizationId}/banners/${banner.id}`
                : `/organizations/${organizationId}/banners`;

            await apiRequest(url, {
                method: banner ? 'PUT' : 'POST',
                body: formDataToSend
            });

            onSuccess();
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Banner kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {banner ? 'Banner Düzenle' : 'Yeni Banner'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                Başlık
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Açıklama
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Media */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Görsel/Video (JPG, GIF, MP4) *
                            </label>
                            <div className="flex items-center space-x-4 mb-2">
                                {mediaPreview && (
                                    <div className="w-48 h-32 relative">
                                        {formData.media_type === 'video' ? (
                                            <video 
                                                src={mediaPreview.startsWith('blob:') || mediaPreview.startsWith('data:') ? mediaPreview : getImageUrl(mediaPreview)} 
                                                className="w-full h-full object-cover rounded-lg" 
                                                controls
                                                onError={(e) => {
                                                    console.error('Video preview error:', e);
                                                    setError('Video önizlemesi yüklenemedi.');
                                                }}
                                            />
                                        ) : (
                                            <img 
                                                src={mediaPreview.startsWith('blob:') || mediaPreview.startsWith('data:') ? mediaPreview : getImageUrl(mediaPreview)} 
                                                alt="Preview" 
                                                className="w-full h-full object-cover rounded-lg"
                                                onError={(e) => {
                                                    console.error('Image preview error:', e);
                                                    setError('Görsel önizlemesi yüklenemedi.');
                                                }}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMedia(null);
                                                setMediaPreview(null);
                                                const input = document.querySelector('input[type="file"]');
                                                if (input) input.value = '';
                                            }}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*,video/*"
                                    required={!banner}
                                    onChange={handleMediaChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            </div>
                            <select
                                name="media_type"
                                value={formData.media_type}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="image">Resim</option>
                                <option value="gif">GIF</option>
                                <option value="video">Video</option>
                            </select>
                        </div>

                        {/* Link URL */}
                        <div>
                            <label htmlFor="link_url" className="block text-sm font-medium text-gray-700 mb-2">
                                Link URL (Opsiyonel)
                            </label>
                            <input
                                type="url"
                                id="link_url"
                                name="link_url"
                                value={formData.link_url}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Display Duration */}
                        <div>
                            <label htmlFor="display_duration" className="block text-sm font-medium text-gray-700 mb-2">
                                Gösterim Süresi (saniye)
                            </label>
                            <input
                                type="number"
                                id="display_duration"
                                name="display_duration"
                                min="1"
                                value={formData.display_duration}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Başlangıç Tarihi
                                </label>
                                <input
                                    type="date"
                                    id="start_date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Bitiş Tarihi
                                </label>
                                <input
                                    type="date"
                                    id="end_date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Time Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-2">
                                    Başlangıç Saati
                                </label>
                                <input
                                    type="time"
                                    id="start_time"
                                    name="start_time"
                                    value={formData.start_time}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-2">
                                    Bitiş Saati
                                </label>
                                <input
                                    type="time"
                                    id="end_time"
                                    name="end_time"
                                    value={formData.end_time}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Active Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Aktif Günler
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {days.map(day => (
                                    <label key={day.value} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.active_days.includes(day.value)}
                                            onChange={() => handleDayToggle(day.value)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{day.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                Durum
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="active">Aktif</option>
                                <option value="inactive">Pasif</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BannerFormModal;

